import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  getClientCheckIns,
  getMe,
  triggerCall,
  deleteClientMemory,
  setClientInteractionType,
  setClientIdentityMode,
} from '../api/client';
import {
  INTERACTION_TYPES,
  INTERACTION_TYPE_LABELS,
  INTERACTION_TYPE_DESCRIPTIONS,
  IDENTITY_MODES,
  IDENTITY_MODE_LABELS,
  IDENTITY_MODE_DESCRIPTIONS,
  type ClientCheckInHistory,
  type InteractionType,
  type IdentityMode,
  type SubscriberMe,
} from '../api/types';
import { useAuth } from '../auth/useAuth';
import { DEBUG } from '../debug';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { ScheduleSection } from '../components/ScheduleSection';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { OutcomeBadge } from '../components/OutcomeBadge';
import { FollowUpBadge } from '../components/FollowUpBadge';
import { ConcernChips } from '../components/ConcernChips';

// Community staff can view residents and trigger calls, but interaction-type,
// identity-mode, and memory deletion are admin/individual-only (backend
// already enforces this with a 403; this gates the UX to match).
export function canEdit(me: SubscriberMe | undefined): boolean {
  if (!me) return false;
  return me.accountType !== 'community' || me.role === 'admin';
}

export function ClientDetailPage() {
  const { memberId } = useParams<{ memberId: string }>();
  const { getIdToken } = useAuth();

  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => getMe(getIdToken) });
  const editable = canEdit(meQuery.data);
  // The detail page is shared; send community users back to their Residents
  // roster (not the individual "Family" view).
  const isCommunity = meQuery.data?.accountType === 'community';
  const backTo = isCommunity ? '/community' : '/clients';
  const backLabel = isCommunity ? 'Back to Residents' : 'Back to family';

  const query = useQuery({
    queryKey: ['client-check-ins', memberId],
    queryFn: () => getClientCheckIns(memberId!, getIdToken),
    enabled: Boolean(memberId),
  });

  const queryClient = useQueryClient();
  const [debugNote, setDebugNote] = useState<string | null>(null);
  const [debugError, setDebugError] = useState<string | null>(null);
  const [interactionNote, setInteractionNote] = useState<string | null>(null);
  const [interactionError, setInteractionError] = useState<string | null>(null);
  const [identityNote, setIdentityNote] = useState<string | null>(null);
  const [identityError, setIdentityError] = useState<string | null>(null);

  const interactionMutation = useMutation({
    onMutate: () => {
      setInteractionNote(null);
      setInteractionError(null);
    },
    mutationFn: (next: InteractionType) => setClientInteractionType(memberId!, next, getIdToken),
    onSuccess: (res) => {
      queryClient.setQueryData<ClientCheckInHistory>(['client-check-ins', memberId], (old) =>
        old ? { ...old, interactionType: res.interactionType } : old,
      );
      setInteractionNote('Saved.');
    },
    onError: () => setInteractionError('Could not save. Please try again.'),
  });

  const identityMutation = useMutation({
    onMutate: () => {
      setIdentityNote(null);
      setIdentityError(null);
    },
    mutationFn: (next: IdentityMode) => setClientIdentityMode(memberId!, next, getIdToken),
    onSuccess: (res) => {
      queryClient.setQueryData<ClientCheckInHistory>(['client-check-ins', memberId], (old) =>
        old ? { ...old, identityMode: res.identityMode } : old,
      );
      setIdentityNote('Saved.');
    },
    onError: () => setIdentityError('Could not save. Please try again.'),
  });

  const triggerMutation = useMutation({
    onMutate: () => {
      setDebugNote(null);
      setDebugError(null);
    },
    mutationFn: () => triggerCall(memberId!, getIdToken),
    onSuccess: () => {
      setDebugError(null);
      setDebugNote(
        `Calling ${query.data?.displayName ?? 'them'}… the check-in will appear here once the call completes.`,
      );
      queryClient.invalidateQueries({ queryKey: ['client-check-ins', memberId] });
    },
    onError: () => setDebugError('Could not start the call. Please try again.'),
  });

  const deleteMemoryMutation = useMutation({
    onMutate: () => {
      setDebugNote(null);
      setDebugError(null);
    },
    mutationFn: () => deleteClientMemory(memberId!, getIdToken),
    onSuccess: () => {
      setDebugError(null);
      setDebugNote('Memory cleared — the next call will start fresh.');
    },
    onError: () => setDebugError('Could not clear memory. Please try again.'),
  });

  if (query.isLoading) return <p className="text-hs-text-mute">Loading…</p>;
  if (query.error) {
    return (
      <p role="alert" className="text-hs-danger-text">
        We couldn't load this person's check-ins right now. Please refresh to try again.
      </p>
    );
  }
  if (!query.data) return null;

  const { displayName, checkIns, interactionType, identityMode } = query.data;

  return (
    <>
      <Link
        to={backTo}
        className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-hs-primary no-underline mb-3"
      >
        <Icon name="back" size={16} /> {backLabel}
      </Link>
      <h1 className="font-display font-bold tracking-hs-tight text-[27px] text-hs-text">{displayName}</h1>
      <p className="mt-1 mb-6 text-[14px] text-hs-text-mute">
        {checkIns.length === 1 ? '1 check-in' : `${checkIns.length} check-ins`}
      </p>

      {editable && (
        <>
          <Card className="mb-6 p-4 flex flex-col gap-2">
            <label
              htmlFor="interaction-type"
              className="text-[12px] font-semibold uppercase tracking-wide text-hs-text-mute"
            >
              Interaction type
            </label>
            <select
              id="interaction-type"
              className="w-full max-w-xs rounded-hs-md border border-hs-border bg-hs-surface px-3 py-2 text-[14px] text-hs-text disabled:opacity-60"
              value={interactionType}
              disabled={interactionMutation.isPending}
              onChange={(e) => interactionMutation.mutate(e.target.value as InteractionType)}
            >
              {INTERACTION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {INTERACTION_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
            <p className="text-[13px] text-hs-text-mute">{INTERACTION_TYPE_DESCRIPTIONS[interactionType]}</p>
            {interactionNote && <p className="text-[13px] text-hs-success-text">{interactionNote}</p>}
            {interactionError && (
              <p className="text-[13px] text-hs-danger-text" role="alert">
                {interactionError}
              </p>
            )}
          </Card>

          <Card className="mb-6 p-4 flex flex-col gap-2">
            <label
              htmlFor="identity-mode"
              className="text-[12px] font-semibold uppercase tracking-wide text-hs-text-mute"
            >
              Introduces itself
            </label>
            <select
              id="identity-mode"
              className="w-full max-w-xs rounded-hs-md border border-hs-border bg-hs-surface px-3 py-2 text-[14px] text-hs-text disabled:opacity-60"
              value={identityMode}
              disabled={identityMutation.isPending}
              onChange={(e) => identityMutation.mutate(e.target.value as IdentityMode)}
            >
              {IDENTITY_MODES.map((m) => (
                <option key={m} value={m}>
                  {IDENTITY_MODE_LABELS[m]}
                </option>
              ))}
            </select>
            <p className="text-[13px] text-hs-text-mute">{IDENTITY_MODE_DESCRIPTIONS[identityMode]}</p>
            {identityNote && <p className="text-[13px] text-hs-success-text">{identityNote}</p>}
            {identityError && (
              <p className="text-[13px] text-hs-danger-text" role="alert">
                {identityError}
              </p>
            )}
          </Card>
        </>
      )}

      {memberId && <ScheduleSection memberId={memberId} editable={editable} />}

      {DEBUG && (
        <div className="mb-6 rounded-hs-md border border-dashed border-hs-border bg-hs-surface-alt p-4">
          <p className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-hs-text-mute">
            Debug
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="ghost"
              loading={triggerMutation.isPending}
              onClick={() => {
                if (window.confirm(`Call ${displayName} now?`)) triggerMutation.mutate();
              }}
            >
              Trigger a call now
            </Button>
            {editable && (
              <Button
                variant="ghost"
                loading={deleteMemoryMutation.isPending}
                onClick={() => {
                  if (
                    window.confirm(`Delete ${displayName}'s learned memory? Their check-in history is kept.`)
                  ) {
                    deleteMemoryMutation.mutate();
                  }
                }}
              >
                Delete memory
              </Button>
            )}
          </div>
          {debugNote && <p className="mt-2 text-[13px] text-hs-success-text">{debugNote}</p>}
          {debugError && (
            <p className="mt-2 text-[13px] text-hs-danger-text" role="alert">
              {debugError}
            </p>
          )}
        </div>
      )}

      {checkIns.length === 0 ? (
        <EmptyState icon="heart" title="No check-ins yet">
          When {displayName} completes a check-in call, it'll show up here.
        </EmptyState>
      ) : (
        <ol className="flex flex-col gap-3 list-none p-0 m-0">
          {checkIns.map((ci) => (
            <Card key={ci.checkInId} as="li" className="p-4 flex flex-col gap-2.5">
              <div className="flex items-center gap-2 flex-wrap">
                <OutcomeBadge outcome={ci.outcome} startedAt={ci.startedAt} />
                <FollowUpBadge value={ci.structured?.recommendedFollowUp ?? 'none'} />
                <span className="ml-auto text-[12px] text-hs-text-mute">
                  {new Date(ci.completedAt ?? ci.startedAt).toLocaleString()}
                </span>
              </div>
              {ci.summary && <p className="text-[14px] text-hs-text leading-relaxed">{ci.summary}</p>}
              <ConcernChips concerns={ci.structured?.concerns ?? []} />
            </Card>
          ))}
        </ol>
      )}
    </>
  );
}
